// Copyright (c) Microsoft. All rights reserved.

import { ContentMetadataArray } from './ContentMetadata';
import { TokenUsage } from './TokenUsage';
import { ResponseStyle } from './UserPreferences';

/**
 * Role of the author of a chat message. It's a copy of AuthorRoles in the API C# code.
 */
export enum AuthorRoles {
    // The current user of the chat.
    User = 0,

    // The bot.
    Bot,

    // The participant who is not the current user nor the bot of the chat.
    Participant,

    // The system itself — broadcast notices such as "agent joined the chat" or "chat ended".
    System,
}

/**
 * Semantic event a System message represents. Mirrors ChatMessage.SystemNoticeKind in the
 * API C# code (emitted as a string on the wire). The client maps it to a localized string;
 * the message's content field is the English fallback for unrecognized values.
 */
export type SystemNoticeKind =
    | 'AgentJoined'
    | 'ChatResolved'
    | 'ChatTransferred'
    | 'QueueTimedOut'
    | 'ChatClosedByCustomer'
    | 'ScreenshotShared'
    | 'ScreenshotDeclined';

/**
 * Which server trigger flagged a message for the inline CSAT widget. Mirrors
 * ChatMessage.CsatTriggers in the API C# code (#2638).
 */
export type CsatTrigger = 'Resolution';

/**
 * Type of the chat message. A copy of ChatMessageType in the API C# code.
 */
export enum ChatMessageType {
    Message,
}

/**
 * States for RLHF
 */
export enum UserFeedback {
    Unknown,
    Requested,
    Positive,
    Negative,
}

/**
 * Which surface the user rated from: the in-message 👍/👎 thumbs (`'thumbs'`) or the embedded
 * chat's header "Hjälpte svaret?" Ja/Nej prompt (`'header'`). Sent alongside the rating so the
 * two cohorts can be told apart in analytics, mirroring how CSAT distinguishes modal vs inline.
 * The closed value set is enforced server-side.
 */
export type UserFeedbackSource = 'thumbs' | 'header';

/**
 * Citation for the response
 */
export interface Citation {
    link: string;
    sourceName: string;
    snippet: string;
    relevanceScore: number;
}

/**
 * A card the Spiris route needs the user to act on, rendered under its bot message: sign in again or
 * turn the MCP on (connect), or approve the write actions the agent wants to run. `kind` and `status` are open strings on the
 * wire; unknown values render nothing. Everything on an approval card is rendered server-side from
 * the stored tool call, never by the model.
 */
export interface SpirisPrompt {
    kind: 'connect' | 'approval' | (string & {});
    reason?: string | null;
    approvalId?: string | null;
    // For an activation_required connect prompt: the configured Spiris page where the company turns access on.
    activationUrl?: string | null;
    actions: SpirisPromptAction[];
    status: 'pending' | 'approved' | 'denied' | 'expired' | (string & {});
}

export interface SpirisPromptAction {
    title: string;
    arguments: Array<{ name: string; value: string }>;
}

export interface IChatMessage {
    chatId: string;
    type: ChatMessageType;
    timestamp: number;
    userName: string;
    userId: string;
    content: string;
    id?: string;
    prompt?: string;
    citations?: Citation[];
    followUpAsks?: string[];
    authorRole: AuthorRoles;
    /**
     * Set only on System messages — the semantic event the notice represents. The UI
     * localizes it in the active language; falls back to `content` when absent/unknown.
     */
    systemNotice?: SystemNoticeKind;
    debug?: string;
    // TODO: [Issue #42] Persistent RLHF
    userFeedback?: UserFeedback;
    tokenUsage?: TokenUsage;
    contentMetadataArray?: ContentMetadataArray;
    graphData?: GraphData;
    tableData?: TableData;
    datasets?: Dataset[];
    attachments?: Attachment[];
    toolCalls?: ToolCall[];
    /**
     * Verbosity that produced this bot answer (#1551). Lets the restyle button decide which
     * direction (shorter/longer) to offer. Undefined for messages stored before the field was
     * introduced — the UI then falls back to the inverse of the user's default preference.
     */
    style?: ResponseStyle;
    /**
     * UI-only data URL preview for an image the user pasted with this message (issue #1504), or
     * one a LiveChat agent sent. NEVER serialized to the backend or persisted. After a page refresh
     * this field is gone — the message rerenders as text-only, or not at all for an agent image.
     */
    localImagePreview?: string;
    /**
     * UI-only marker for the local bubble of an image sent to a human agent over LiveChat. Never
     * persisted; lets the composer keep such rows out of ArrowUp history without also dropping a
     * pasted-image message the customer actually typed.
     */
    isLocalEcho?: boolean;
    /**
     * When true, the in-chat CSAT widget renders below this bot message (#1641). Set by
     * the workflow on positive/done sentiment or when an agent resolves the LiveChat. Persisted
     * on the server message so it survives refresh.
     */
    showCsatWidget?: boolean;
    /**
     * Which trigger set showCsatWidget: 'Resolution' when an agent resolved the LiveChat (#2638),
     * absent for the sentiment path.
     */
    csatTrigger?: CsatTrigger | null;
    /**
     * True once the user has answered or dismissed the inline widget, so subsequent renders keep
     * it collapsed (#1641). Set locally on submit/dismiss for an instant round-trip-free hide, and
     * also persisted on the server message by the submit/decline endpoints so the hidden state
     * survives a page reload or re-opening the chat from history.
     */
    csatSubmitted?: boolean;
    /** Spiris connect/approval card for this bot message; its status is updated live over SignalR. */
    spirisPrompt?: SpirisPrompt | null;
    /** `voice` on a question spoken in voice mode and on its spoken answer (#2578); absent for text. */
    modality?: string;
}

/**
 * True once a human agent has joined the conversation (or it was resolved) — a System notice
 * marks that transition (#1599, #1769). Lets the UI gate bot-only behaviour once the chat is in
 * human hands: hide the now-stale proactive escalation offer, and skip the "land at the top of
 * the latest bot answer" scroll (which would otherwise yank the view up to the pre-handoff AI
 * answer when the customer sends a message to the agent).
 */
export const hasAgentJoinedOrResolved = (messages: IChatMessage[]): boolean =>
    messages.some((m) => m.systemNotice === 'AgentJoined' || m.systemNotice === 'ChatResolved');

/**
 * True once the escalation episode has ended for any reason — a human joined, the chat was
 * resolved, the queue timed out, or the customer abandoned it (#1937). Used to retire the proactive
 * offer's stale "waiting for an agent" card. Broader than hasAgentJoinedOrResolved, which
 * deliberately ignores timeouts because it also gates post-handoff scroll behaviour (after a timeout
 * the customer is back on the AI and that scroll should resume). ChatClosedByCustomer must be here
 * too: a chat abandoned while still Queued carries only that notice (no AgentJoined/ChatResolved/
 * QueueTimedOut), so without it the offer card would linger on the already-closed, read-only chat.
 */
export const hasEscalationEnded = (messages: IChatMessage[] | undefined): boolean =>
    !!messages &&
    (hasAgentJoinedOrResolved(messages) ||
        messages.some((m) => m.systemNotice === 'QueueTimedOut' || m.systemNotice === 'ChatClosedByCustomer'));

/**
 * True while a human agent is actively handling the conversation: the most recent System notice
 * is 'AgentJoined' with no 'ChatResolved' after it (#1770). Unlike hasAgentJoinedOrResolved —
 * which stays true forever once either notice has appeared — this flips back to false when the
 * agent resolves the chat, so AI-only UI (workflow status card, stop button) returns when the
 * customer goes back to talking with the assistant. The last notice wins, which also covers
 * re-escalation (resolved → queued → a new agent joins).
 */
export const isAgentSessionActive = (messages: IChatMessage[]): boolean => {
    for (let i = messages.length - 1; i >= 0; i--) {
        const notice = messages[i].systemNotice;
        if (notice === 'AgentJoined') return true;
        // Every terminal notice must end the session here, or the scan falls through to an earlier
        // AgentJoined and falsely reports an active agent on an already-closed chat (#1937).
        if (notice === 'ChatResolved' || notice === 'QueueTimedOut' || notice === 'ChatClosedByCustomer') return false;
    }
    return false;
};

/**
 * True once a human-handover chat has been resolved and is now a read-only historic conversation:
 * the newest System notice is 'ChatResolved' (agent-resolved, #1938) or 'ChatClosedByCustomer' (the
 * customer abandoned it by starting a new chat, #1937). Mirrors isAgentSessionActive's "last notice
 * wins" scan, so re-escalation (resolved -> queued -> AgentJoined) re-opens the composer, and a
 * queue timeout (customer bounced back to the AI) never locks it.
 */
/**
 * The newest System notice that says something about the handover's state. Screenshot outcomes
 * are skipped: one can land just after the close (an upload racing a resolve) and says nothing
 * about whether the chat is open.
 */
export const newestHandoverNotice = (messages: IChatMessage[]): SystemNoticeKind | undefined => {
    for (let i = messages.length - 1; i >= 0; i--) {
        const notice = messages[i].systemNotice;
        if (notice === 'ScreenshotShared' || notice === 'ScreenshotDeclined') continue;
        if (notice) return notice;
    }
    return undefined;
};

export const isChatClosedAfterHandover = (messages: IChatMessage[]): boolean => {
    const notice = newestHandoverNotice(messages);
    return notice === 'ChatResolved' || notice === 'ChatClosedByCustomer';
};

/**
 * Index of the 'QueueTimedOut' notice whose callback offer is current, or -1. The offer belongs to
 * the moment right after the timeout: a later handover notice, or the customer writing again, ends
 * it. Screenshot outcomes are skipped as in newestHandoverNotice.
 */
export const currentQueueTimeoutIndex = (messages: IChatMessage[]): number => {
    for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (message.authorRole === AuthorRoles.User) return -1;
        if (message.systemNotice === 'ScreenshotShared' || message.systemNotice === 'ScreenshotDeclined') continue;
        if (message.systemNotice) return message.systemNotice === 'QueueTimedOut' ? i : -1;
    }
    return -1;
};

/**
 * Folds a server snapshot of a chat into the local message list after a SignalR reconnect. Replacing
 * the list wholesale lost two things:
 *
 *  - Messages pushed after the server read its snapshot. Pushes resume as soon as the client rejoins
 *    the chat's group, before the snapshot request returns.
 *  - localImagePreview, which the server never stores.
 *
 * The server copy wins for every message both sides have. Local messages the snapshot lacks are kept:
 * those ahead of the first shared message predate the snapshot's window, those after the last shared
 * one arrived after it, and one between two shared messages (an own send the server rejected with a
 * 429 or 409) stays behind the message it followed. With nothing shared their age can't be told, so
 * they go after the snapshot, where a push would land.
 * A message this tab sent has no id until a reload, so a local user message without one is paired with
 * the first unpaired server user message with the same content (the server stores the input verbatim).
 */
export const mergeBackfilledMessages = (local: IChatMessage[], snapshot: IChatMessage[]): IChatMessage[] => {
    const localById = new Map<string, IChatMessage>();
    const unpairedOwn: IChatMessage[] = [];
    for (const message of local) {
        if (message.id) {
            localById.set(message.id, message);
        } else if (message.authorRole === AuthorRoles.User) {
            unpairedOwn.push(message);
        }
    }

    const paired = new Set<IChatMessage>();
    // Index-aligned with the snapshot: the local message each server message replaces, if any.
    const pairedLocal: Array<IChatMessage | undefined> = [];
    const merged = snapshot.map((serverMessage) => {
        let localMessage = serverMessage.id ? localById.get(serverMessage.id) : undefined;
        if (!localMessage && serverMessage.authorRole === AuthorRoles.User) {
            const index = unpairedOwn.findIndex((m) => m.content === serverMessage.content);
            if (index >= 0) {
                localMessage = unpairedOwn.splice(index, 1)[0];
            }
        }
        pairedLocal.push(localMessage);
        if (!localMessage) {
            return serverMessage;
        }
        paired.add(localMessage);
        return localMessage.localImagePreview
            ? { ...serverMessage, localImagePreview: localMessage.localImagePreview }
            : serverMessage;
    });

    const firstPaired = local.findIndex((m) => paired.has(m));
    if (firstPaired < 0) {
        return [...merged, ...local];
    }

    // Each shared local message's unshared followers, up to the next shared one. What is left in `tail`
    // after the loop followed the last shared message.
    const followers = new Map<IChatMessage, IChatMessage[]>();
    let anchor = local[firstPaired];
    let tail: IChatMessage[] = [];
    for (const message of local.slice(firstPaired + 1)) {
        if (paired.has(message)) {
            followers.set(anchor, tail);
            anchor = message;
            tail = [];
        } else {
            tail.push(message);
        }
    }

    return [
        ...local.slice(0, firstPaired),
        ...merged.flatMap((message, i) => {
            const localMessage = pairedLocal[i];
            return [message, ...((localMessage ? followers.get(localMessage) : undefined) ?? [])];
        }),
        ...tail,
    ];
};

/**
 * Feedback-prompt view of a conversation, used to decide whether a "was this answer helpful?"
 * prompt should appear and which message it targets. Kept next to isAgentSessionActive — and
 * reusing it — so the handover semantics live in one tested place: the inline-header prompt and
 * any other feedback surface can't drift apart on what counts as "an answer that's now in human
 * hands" when the state machine changes (re-escalation, transfer #1809, resolve → new agent).
 */
export interface BotAnswerFeedbackState {
    /**
     * Most recent bot *answer* — a Bot message that follows a User message, so the opening
     * greeting never counts. Undefined until the bot has answered a user turn.
     */
    latestAnswer?: IChatMessage;
    /**
     * True while a human agent is handling the chat: an agent session is active (AgentJoined with
     * no later ChatResolved), or a Participant message landed after the latest bot answer — which
     * covers a just-resolved handover whose stale pre-handover answer is still the latest bot
     * turn. After resolution a genuinely new bot answer clears this and re-enables the prompt.
     */
    agentHandling: boolean;
}

export const botAnswerFeedbackState = (messages: IChatMessage[]): BotAnswerFeedbackState => {
    let latestAnswer: IChatMessage | undefined;
    let sawUser = false;
    // Did a human agent speak after the most recent bot answer? Reset whenever we pass a fresh
    // bot answer so it reflects only activity since that answer.
    let agentSinceLastAnswer = false;
    for (const message of messages) {
        if (message.authorRole === AuthorRoles.User) {
            sawUser = true;
            continue;
        }
        if (message.authorRole === AuthorRoles.Participant) {
            agentSinceLastAnswer = true;
            continue;
        }
        if (message.authorRole !== AuthorRoles.Bot || !sawUser) {
            continue;
        }
        latestAnswer = message;
        agentSinceLastAnswer = false;
    }
    return {
        latestAnswer,
        agentHandling: agentSinceLastAnswer || isAgentSessionActive(messages),
    };
};

export interface Dataset {
    datasetReferenceId: string;
    toolCallName: string;
    toolCallArguments: string;
    expirationTime?: Date;
    headers?: string[];
    sqlQuery?: string;
}

export interface BaseAttachment {
    name: string;
    attachmentType: string;
    datasetReferenceId: string;
    explanation?: CalculationGuide;
    creatingExplanation: boolean;
}

export interface TableAttachment extends BaseAttachment {
    attachmentType: 'table';
    columnsToShow: string;
}

export interface ChartMappingSpec {
    labelColumnIndex: number;
    dataColumnIndexes: number[];
    columnNames: string[];
}

export interface ChartAttachment extends BaseAttachment {
    attachmentType: 'chart';
    chartType: string;
    config: string;
    // Declarative mapping spec the frontend uses to shape rows into chart.js datasets (#1817).
    // Replaces the legacy mappingFunction JS string that was eval'd in useChartData. Optional:
    // chart attachments persisted before this change won't carry it.
    mapping?: ChartMappingSpec;
}

// Union type for all possible attachments
export type Attachment = TableAttachment | ChartAttachment;

export interface GraphData {
    labels: string[];
    datasets: Dataset[];
}

export interface TableData {
    datasetReferenceId: string;
}

export interface ToolCall {
    functionArguments: string;
    functionName: string;
    id: string;
}

export interface CalculationGuide {
    title: string;
    summary: string;
    calculations: CalculationSection[];
    note: string;
}

export interface CalculationSection {
    title: string;
    actions: string[];
}
